/** Availability rules of a booking action (Actions → booking action → Availability). */
export const availability = {
  title: "Availability",
  subtitle: "When visitors can book and how bookings get confirmed.",
  banner: {
    calendar: {
      title: "Bookings are created in Google Calendar",
      body: "Free times come from {{calendar}}. Each booking becomes a calendar event.",
      bodyNoName: "Free times come from your connected calendar. Each booking becomes a calendar event.",
    },
    request: {
      title: "Visitors send a booking request",
      body: "Your team accepts or declines each one in Leads → Bookings.",
      connectHint: "Connect Google Calendar to confirm times automatically.",
      connect: "Connect Google Calendar",
      open: "Open bookings",
    },
    unconfigured: {
      title: "Visitors are not offered times yet",
      body: "Set your opening hours below and save. Until then the assistant can't offer any appointment times.",
      cta: "Set opening hours",
    },
  },
  mode: {
    label: "How bookings are confirmed",
    auto: "Automatic",
    autoHint: "Uses Google Calendar when it's connected. Without a calendar, visitors send a request and your team confirms it.",
    request: "Always ask staff to confirm",
    requestHint: "Visitors request a time and your team accepts or declines it in Leads → Bookings, even when a calendar is connected.",
  },
  timezone: {
    label: "Time zone",
    workspace: "Workspace time zone ({{zone}})",
    hint: "Opening hours below follow this time zone.",
  },
  hours: {
    title: "Weekly hours",
    hint: "Add up to {{count}} time ranges per day. Leave a day closed when nobody takes bookings.",
    closed: "Closed",
    openToggle: "{{day}} open",
    opens: "{{day}}, range {{number}}: opens at",
    closes: "{{day}}, range {{number}}: closes at",
    add: "Add time range",
    addLabel: "Add a time range on {{day}}",
    remove: "Remove range {{number}} on {{day}}",
    copy: "Copy to Mon–Fri",
    copyLabel: "Copy {{day}} hours to Monday through Friday",
    copied: "{{day}} hours copied to Monday–Friday",
    days: {
      mon: "Monday",
      tue: "Tuesday",
      wed: "Wednesday",
      thu: "Thursday",
      fri: "Friday",
      sat: "Saturday",
      sun: "Sunday",
    },
  },
  timing: {
    slotMinutes: "Appointment length (minutes)",
    slotMinutesHint: "10 to 240",
    bufferMinutes: "Buffer between bookings (minutes)",
    bufferMinutesHint: "0 to 120",
    minNoticeHours: "Minimum notice (hours)",
    minNoticeHoursHint: "0 to 720. Visitors can't book sooner than this.",
    horizonDays: "Bookable up to (days ahead)",
    horizonDaysHint: "1 to 180",
  },
  blocked: {
    title: "Blocked dates",
    hint: "Holidays and other days you're closed (up to {{count}}).",
    dateLabel: "Date to block",
    add: "Block date",
    remove: "Unblock {{date}}",
    list: "Blocked dates",
    none: "No blocked dates.",
    duplicate: "That date is already blocked.",
    full: "You can block up to {{count}} dates.",
    invalid: "Choose a valid date.",
  },
  location: {
    label: "Location",
    hint: "Where the booking happens. Shown to the visitor and in the calendar event.",
    placeholder: "Admissions Office, 2nd floor",
  },
  meet: {
    label: "Add a Google Meet link",
    hint: "Calendar bookings get a video link, handy for online consultations.",
  },
  save: "Save availability",
  saved: "Availability saved",
  readOnly: "You can view these rules, but changing them needs permission to manage actions.",
  errors: {
    clock: "Enter a time like 09:00.",
    rangeOrder: "Each time range must end after it starts.",
    overlap: "Time ranges on one day must not overlap.",
    maxRanges: "At most {{count}} time ranges per day.",
    noOpenDay: "Open at least one day of the week.",
    slotMinutes: "Enter a whole number from 10 to 240 minutes.",
    bufferMinutes: "Enter a whole number from 0 to 120 minutes.",
    minNoticeHours: "Enter a whole number from 0 to 720 hours.",
    horizonDays: "Enter a whole number from 1 to 180 days.",
    blockedMax: "You can block up to {{count}} dates.",
    locationMax: "Keep the location under {{count}} characters.",
    timezone: "Choose a valid time zone, for example Asia/Manila.",
  },
} as const;
